import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { router } from '../services/router.js';

interface SkillPromotionCandidate {
  id: number;
  source_agent_id: number;
  pattern_tag: string;
  related_memory_ids: string;
  cluster_count: number;
  proposed_skill_title: string | null;
  proposed_skill_content: string | null;
  status: string;
  curator_decision_reason: string | null;
  user_decision_reason: string | null;
  created_at: string;
  updated_at: string;
}

type ViewState = 'loading' | 'loaded' | 'not_found' | 'error' | 'confirmed' | 'rejected';

@customElement('cp-page-skill-promotion')
export class CpPageSkillPromotion extends LitElement {
  createRenderRoot() { return this; }

  @state() private _candidate: SkillPromotionCandidate | null = null;
  @state() private _viewState: ViewState = 'loading';
  @state() private _error = '';
  @state() private _working = false;
  @state() private _candidateId = 0;

  connectedCallback(): void {
    super.connectedCallback();
    const params = router.getParams();
    this._candidateId = Number(params['id'] ?? 0);
    if (!this._candidateId) {
      this._viewState = 'not_found';
      return;
    }
    void this._load();
  }

  private async _load(): Promise<void> {
    this._viewState = 'loading';
    try {
      const res = await fetch(`/api/memory/skill-promotion/${this._candidateId}`);
      if (res.status === 404) {
        this._viewState = 'not_found';
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this._candidate = await res.json() as SkillPromotionCandidate;
      this._viewState = this._candidate.status === 'user_confirmed'
        ? 'confirmed'
        : this._candidate.status === 'user_rejected'
          ? 'rejected'
          : 'loaded';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Ladefehler';
      this._viewState = 'error';
    }
  }

  private async _confirm(): Promise<void> {
    if (this._working || !this._candidate) return;
    this._working = true;
    try {
      const res = await fetch(`/api/memory/skill-promotion/${this._candidate.id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        this._error = data.error ?? `HTTP ${res.status}`;
        return;
      }
      this._viewState = 'confirmed';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Bestätigen';
    } finally {
      this._working = false;
    }
  }

  private async _reject(): Promise<void> {
    if (this._working || !this._candidate) return;
    this._working = true;
    try {
      const res = await fetch(`/api/memory/skill-promotion/${this._candidate.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        this._error = data.error ?? `HTTP ${res.status}`;
        return;
      }
      this._viewState = 'rejected';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Ablehnen';
    } finally {
      this._working = false;
    }
  }

  render() {
    return html`
      <div class="cp-page skill-promotion-page">
        <div class="skill-promotion-container">
          ${this._renderContent()}
        </div>
      </div>
    `;
  }

  private _renderContent() {
    switch (this._viewState) {
      case 'loading':
        return html`<p class="skill-promotion-status">Lade Skill-Vorschlag…</p>`;

      case 'not_found':
        return html`<p class="skill-promotion-status skill-promotion-status--error">Skill-Vorschlag nicht gefunden.</p>`;

      case 'error':
        return html`<p class="skill-promotion-status skill-promotion-status--error">Fehler: ${this._error}</p>`;

      case 'confirmed':
        return html`
          <div class="skill-promotion-result skill-promotion-result--success">
            <span class="skill-promotion-icon">✅</span>
            <h2>Skill bestätigt!</h2>
            <p>Der Skill wird verteilt und steht Agenten beim nächsten Spawn zur Verfügung.</p>
          </div>
        `;

      case 'rejected':
        return html`
          <div class="skill-promotion-result">
            <span class="skill-promotion-icon">❌</span>
            <h2>Skill abgelehnt.</h2>
            <p>Der Vorschlag wurde abgelehnt und wird nicht verteilt.</p>
          </div>
        `;

      case 'loaded':
        return this._renderDetail();

      default:
        return nothing;
    }
  }

  private _renderDetail() {
    const c = this._candidate;
    if (!c) return nothing;
    const title = c.proposed_skill_title ?? c.pattern_tag;

    return html`
      <div class="skill-promotion-card">
        <header class="skill-promotion-header">
          <span class="skill-promotion-badge">🎯 Skill-Vorschlag</span>
          <h1 class="skill-promotion-title">${title}</h1>
        </header>

        <dl class="skill-promotion-meta">
          <dt>Pattern</dt>
          <dd><code>${c.pattern_tag}</code></dd>
          <dt>Cluster-Größe</dt>
          <dd>${c.cluster_count} Erinnerungen</dd>
          ${c.curator_decision_reason ? html`
            <dt>Curator-Begründung</dt>
            <dd>${c.curator_decision_reason}</dd>
          ` : nothing}
        </dl>

        ${c.proposed_skill_content ? html`
          <details class="skill-promotion-preview">
            <summary>Skill-Inhalt ansehen</summary>
            <pre>${c.proposed_skill_content}</pre>
          </details>
        ` : nothing}

        ${this._error ? html`<p class="skill-promotion-error">${this._error}</p>` : nothing}

        <div class="skill-promotion-actions">
          <button
            class="skill-promotion-btn skill-promotion-btn--confirm"
            ?disabled=${this._working}
            @click=${this._confirm}
          >
            ${this._working ? 'Wird verarbeitet…' : '✅ Skill bestätigen'}
          </button>
          <button
            class="skill-promotion-btn skill-promotion-btn--reject"
            ?disabled=${this._working}
            @click=${this._reject}
          >
            ❌ Ablehnen
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-skill-promotion': CpPageSkillPromotion;
  }
}
